import React, { useState, useEffect, useMemo } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import Tools from "../components/Tools";
import Faq from "../components/Faq";
import InBlog from "../components/InBlog";
import ChatButton from "../components/ChatButton";

function WebAudit() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all"); // all | errors | improve | passed

  const location = useLocation();
  
 
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get("url");
    if (q) {
      setUrl(q);
      runAudit(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const runAudit = async (inputUrl) => {
    if (!inputUrl) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      let fullUrl = inputUrl.trim();
      if (!/^https?:\/\//i.test(fullUrl)) {
        fullUrl = "https://" + fullUrl;
      }
      const response = await fetch(
        "https://www.pixelliongroup.com/seotic/seo-audit-v2.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url: fullUrl }),
        }
      );
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      setResult(data);
    } catch (err) {
      setError(err.message || "Failed to fetch audit data");
    } finally {
      setLoading(false);
    }
  };

  const formatKey = (key) =>
    key
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");

  // Treating "inline css check" as an audit item to hide in the UI
  const HIDE_KEYS = new Set([
    "inline_css",
    "inline_style",
    "inline_styles",
    "inline_css_usage",
    "inline_style_usage",
  ]);
  const hideByPattern = (key) => /(inline[_-]?(css|style)s?)/i.test(key);

  const PASSED = new Set(["Perfect", "Good", "Declared", "Present", "Enabled", "Yes", "Provided"]);
  const FAILED = new Set(["Missing", "Not Specified", "Disabled", "No"]);
  const IMPROVE = new Set(["Needs Improvement", "Too Long", "Too Short"]);

  // Normalize all checks into one flat list for grid, excluding inline CSS/style check(s)
  const allChecks = useMemo(() => {
    if (!result) return [];
    const items = [];
    Object.entries(result).forEach(([key, val]) => {
      if (HIDE_KEYS.has(key) || hideByPattern(key)) return; // ← remove inline css check(s)

      if (val && typeof val === "object" && "assessment" in val) {
        const assessment = String(val.assessment || "").trim();
        let status = "passed";
        if (FAILED.has(assessment)) status = "errors";
        else if (IMPROVE.has(assessment)) status = "improve";

        items.push({
          key,
          title: formatKey(key),
          assessment,
          suggestion: val.suggestion || "",
          details: val.details || "",
          status, // "passed" | "improve" | "errors"
        });
      }
    });
    return items.sort((a, b) => {
      // Show PASSED first, then IMPROVE, then ERRORS
      const weight = { passed: 0, improve: 1, errors: 2 }; // ← changed order
      if (weight[a.status] !== weight[b.status]) return weight[a.status] - weight[b.status];
      return a.title.localeCompare(b.title);
    });
  }, [result]);

  const counts = useMemo(() => {
    return {
      all: allChecks.length,
      errors: allChecks.filter((i) => i.status === "errors").length,
      improve: allChecks.filter((i) => i.status === "improve").length,
      passed: allChecks.filter((i) => i.status === "passed").length,
    };
  }, [allChecks]);

  const filtered = useMemo(() => {
    if (activeFilter === "all") return allChecks;
    return allChecks.filter((i) => i.status === activeFilter);
  }, [allChecks, activeFilter]);

  const statusStyles = {
    errors: {
      ring: "ring-red-200",
      badgeBg: "bg-red-50",
      badgeText: "text-red-700",
      dot: "bg-red-500",
      title: "text-red-800",
    },
    improve: {
      ring: "ring-amber-200",
      badgeBg: "bg-amber-50",
      badgeText: "text-amber-800",
      dot: "bg-amber-500",
      title: "text-amber-900",
    },
    passed: {
      ring: "ring-emerald-200",
      badgeBg: "bg-emerald-50",
      badgeText: "text-emerald-800",
      dot: "bg-emerald-500",
      title: "text-emerald-900",
    },
  };

  const StatusIcon = ({ status }) => {
    if (status === "passed") {
      return (
        <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
          <path fill="currentColor" d="M9 16.2l-3.5-3.5L4 14.2l5 5 11-11-1.4-1.4z" />
        </svg>
      );
    }
    if (status === "improve") {
      return (
        <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
          <path fill="currentColor" d="M11 7h2v6h-2zm0 8h2v2h-2z" />
        </svg>
      );
    }
    return (
      <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
        <path fill="currentColor" d="M11 15h2v2h-2zm0-8h2v6h-2z" />
      </svg>
    );
  };

  const FilterPill = ({ id, label, count }) => {
    const active = activeFilter === id;
    return (
      <button
        onClick={() => setActiveFilter(id)}
        className={
          "px-3 py-1.5 rounded-lg text-sm transition border " +
          (active
            ? "bg-white shadow"
            : "bg-slate-50 text-slate-700")
        }
      >
        {label} <span className="ml-1 text-slate-400">{count}</span>
      </button>
    );
  };

  // Donut chart component (SVG circle progress)
  const Donut = ({ percent = 0, size = 120, stroke = 12 }) => {
    const normalized = Math.max(0, Math.min(100, Number(percent) || 0));
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const dash = (normalized / 100) * circumference;

    return (
      <svg width={size} height={size} className="block">
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          {/* Track */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            strokeWidth={stroke}
            stroke="#e5e7eb" // slate-200
            fill="none"
          />
          {/* Progress */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            strokeWidth={stroke}
            strokeLinecap="round"
            stroke="#10b981" // emerald-500
            fill="none"
            strokeDasharray={`${dash} ${circumference - dash}`}
          />
        </g>
        {/* Center text */}
        <text
          x="50%"
          y="50%"
          dominantBaseline="middle"
          textAnchor="middle"
          fontSize="20"
          fontWeight="600"
          fill="#111827" // gray-900
        >
          {normalized}%
        </text>
      </svg>
    );
  };

 const pct = useMemo(() => {
  if (!counts.all) return 0;
  return Math.round((counts.passed / counts.all) * 100);
}, [counts]);

  return (
    <>
      <Helmet>
        <title>Website Audit Tool - Seotic</title>
        <meta name="description" content="Analyze a webpage and receive SEO suggestions" />
      </Helmet>

      <Navbar />

      <div className="px-5 bg-gradient-to-t from-slate-100 to-white">
        <div className="container mx-auto pt-20 pb-5">
          <div className="flex flex-col justify-center items-center mb-8">
            <h2 className="text-center text-base/7 uppercase text-slate-400">PUBLIC TOOL</h2>
            <p className="mx-auto mt-2 max-w-xl text-center text-4xl font-semibold tracking-tight text-pretty text-gray-950 sm:text-5xl">
              Website audit and guide for beginners
            </p>
          </div>

          <p className="text-center text-slate-600 mb-8">Enter your website URL to get an instant SEO audit.</p>

          <div className="flex mb-6 max-w-md mx-auto">
            <input
              type="text"
              placeholder="www.seotic.co"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="flex-grow px-5 py-3 border border-slate-200 mr-2 rounded-lg focus:outline-none"
            />
            <button
              onClick={() => runAudit(url)}
              className="px-5 py-3 bg-yellow-300 text-yellow-900 rounded-lg hover:bg-yellow-400"
            >
              Audit
            </button>
          </div>

          {loading && <p className="text-center mt-10">Analyzing...</p>}
          {error && <p className="text-center text-red-500 mt-10">{error}</p>}

          {result && (
	          <>
	        
	            {/* Summary with donut ring */}
              {result.audit_statistics && (
	                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10 ">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 items-center bg-white border rounded-lg p-10 md:p-8 ">
                 <div className="sm:col-span-1 flex justify-center md:ml-10">
  {/* Overall Score */}
  <div className="relative">
    {/* Background Half-Circle with Gradient */}
    <div className="relative flex justify-center items-center mt-5 z-10">
      <div
        className={`blur-md h-24 w-32 transform -translate-y-1/2
          ${
            pct >= 80
              ? 'bg-gradient-to-t from-white to-green-300'
              : pct >= 50
              ? 'bg-gradient-to-t from-white  to-yellow-300'
              : 'bg-gradient-to-t from-white to-red-300'
          }
          rounded-tl-full rounded-tr-full absolute top-5 z-[-10]`}
      ></div>

      <p className="text-xs uppercase text-white px-5 py-2 z-20 opacity-75 relative bg-slate-900 rounded-full">
        <i className="bx bxs-grid bx-fw"></i>Overall
      </p>
    </div>

    {/* Text Elements */}
    <p className="text-6xl font-bold z-20 text-center relative">
      {String(pct).padStart(2, '0')}
    </p>
    <p className="text-slate-500 text-center text-sm">{String(pct).padStart(2, '0')} out of 100</p>
  </div>
</div>

                  <div className="sm:col-span-2 ml-12">
                     <h3 className="text-2xl font-medium mb-2 text-gray-950">
        {url ? url.replace(/^https?:\/\//, "").replace(/\/$/, "") : "Website Audit"}
      </h3>
                  <p className="text-slate-600">
  Passed: {counts.passed} / {counts.all} ({pct}%)
</p>
<p className="text-slate-600">
  Errors: {counts.errors} · Needs Improvement: {counts.improve}
</p>
                  </div>
                </div>
                
                <div>
	                  <div className="bg-white border rounded-lg p-10 md:p-8">
	                <h3 className="text-2xl font-medium"><i class='bx bx-heart-circle bx-burst bx-fw text-pink-500' ></i> Unlock Full Audit</h3>
	                <p className="mt-5 text-slate-800">Get clear insights, track backlinks, compare competitors, monitor rankings, see detailed analytics and use AI powerful tools to grow your traffic.</p>
	                <div className="flex-cols md:flex items-center">
	                 <a href="https://app.seotic.co/register" title="create account" className="flex items-center uppercase px-5 py-3 md:px-5 md:py-2 bg-yellow-400 text-yellow-700 rounded-lg text-sm rounded hover:text-yellow-900 font-medium justify-between md:w-fit mt-5 mr-5">
              Try Seotic Today <i class='bx bx-right-arrow-circle bx-sm ml-2' ></i>            </a>
              
	                <div className="flex justify-center"> <p className="text-sm text-slate-700 mt-5 flex items-center"><svg xmlns="http://www.w3.org/2000/svg" className="mr-2" viewBox="0 0 24 24" width="24" height="24" fill="none">
    <path d="M2 12C2 8.46252 2 6.69377 3.0528 5.5129C3.22119 5.32403 3.40678 5.14935 3.60746 4.99087C4.86213 4 6.74142 4 10.5 4H13.5C17.2586 4 19.1379 4 20.3925 4.99087C20.5932 5.14935 20.7788 5.32403 20.9472 5.5129C22 6.69377 22 8.46252 22 12C22 15.5375 22 17.3062 20.9472 18.4871C20.7788 18.676 20.5932 18.8506 20.3925 19.0091C19.1379 20 17.2586 20 13.5 20H10.5C6.74142 20 4.86213 20 3.60746 19.0091C3.40678 18.8506 3.22119 18.676 3.0528 18.4871C2 17.3062 2 15.5375 2 12Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
    <path d="M10 16H11.5" stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
    <path d="M14.5 16L18 16" stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
    <path d="M2 9H22" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" />
</svg>No credit card needed!</p>
	                </div>
</div>

	                
	                </div>
                </div>
              </div>
              )}

            <div className="bg-white border rounded-lg p-6 sm:p-8 mt-5">
            
              {/* Filters */}
              <div className="flex flex-wrap gap-2 mb-6">
                <FilterPill id="all" label="All" count={counts.all} />
                <FilterPill id="passed" label="Passed" count={counts.passed} />
                <FilterPill id="improve" label="Needs improvement" count={counts.improve} />
                <FilterPill id="errors" label="Errors" count={counts.errors} />
              </div>

              {/* Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
                {filtered.map((item) => {
                  const s = statusStyles[item.status];
                  return (
                    <div
                      key={item.key}
                      className={`rounded-lg border border-slate-200 bg-white p-5`}
                    >
                      <div className="flex items-center mb-1">
                        
                         <h4 className={`font-semibold text-lg mr-2 ${s.title}`}>{item.title}</h4>
                        <span className={`inline-flex  items-center px-2 py-0.5 rounded-full text-xs font-normal ${s.badgeBg} ${s.badgeText}`}>
                         
                          {item.assessment}
                        </span>
                      </div>
                     
                      {item.suggestion && (
                        <p className="mt-2 text-sm text-slate-700">
                          {item.suggestion}
                        </p>
                      )}
                      {item.details && (
                        <p className="mt-2 text-xs text-slate-500">
                          {item.details}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

                         </div>
        </>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 my-5">
          
          <div className="rounded-lg border border-slate-200 bg-white p-10">
  <h3 className="text-2xl font-medium text-gray-950 mb-4">
    What is a Website Audit?
  </h3>
  <p className="text-slate-500">
    A website audit is a comprehensive review of your site's health,
    covering technical setup, content and on-page elements. It
    uncovers issues that may prevent search engines from properly
    indexing your pages and highlights opportunities to improve
    overall performance.
  </p>
</div>

<div className="rounded-lg border border-slate-200 bg-white p-10">
  <h3 className="text-2xl font-medium text-gray-950 mb-4">
    Why perform a Website Audit?
  </h3>
    <p className="text-slate-500">
    Regular audits help you catch problems before they impact
    rankings, ensure your content follows current best practices and
    reveal quick wins that can boost traffic and conversions.
  </p>
</div>

<div className="rounded-lg border border-slate-200 bg-white p-10">
  <h3 className="text-2xl font-medium text-gray-950 mb-4">
    What happens after a Audit?
  </h3>
    <p className="text-slate-500">
    After an audit, you’ll receive a clear action plan with
    recommendations to fix technical issues, optimize content and
    strengthen your SEO strategy for long-term growth.
  </p>
</div>
          </div>
          
              {/* <Tools  title="Try more Seotic tools" /> */}
        </div>
    
      </div>
  <InBlog />
        <Faq />
        <div className="container mx-auto my-10">
          <ChatButton />
        </div>

      <Footer />
    </>
  );
}

export default WebAudit;
